<template>
  <v-autocomplete
    :model-value="modelValue || null"
    :items="items"
    item-title="name"
    item-value="id"
    :label="label"
    variant="outlined"
    density="compact"
    clearable
    hide-details
    :loading="loading"
    :disabled="disabled"
    class="preferred-tech-field"
    @update:model-value="onChange"
  >
    <template #selection="{ item }">
      <span class="text-body-2 text-truncate">{{ item.title }}</span>
    </template>
  </v-autocomplete>
</template>

<script setup>
/**
 * Optional preferred technician picker for the shared customer profile.
 * Lists domain technicians via the same /hr/employees/?role=technician
 * endpoint the job board uses. Shows the chosen person's name, not a count.
 */
import { computed, onMounted, ref, watch } from 'vue'
import axios from '@/api/client'
import { useWorkerTerm } from '@/composables/useWorkerTerm'

const props = defineProps({
  modelValue: { type: [Number, String], default: null },
  currentName: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
})

const emit = defineEmits(['update:modelValue'])

const { term } = useWorkerTerm()
const loading = ref(false)
const technicians = ref([])

const label = computed(() => `Preferred ${term.value}`)

const items = computed(() => {
  const list = technicians.value.slice()
  const id = props.modelValue == null || props.modelValue === ''
    ? null
    : Number(props.modelValue)
  const name = (props.currentName || '').trim()
  if (id && name && !list.some((t) => Number(t.id) === id)) {
    list.unshift({ id, name })
  }
  return list
})

async function loadTechnicians() {
  loading.value = true
  try {
    const response = await axios.get('/hr/employees/', {
      params: { role: 'technician', is_active: true, page_size: 500 },
    })
    const raw = response.data?.results || response.data || []
    technicians.value = raw.map((t) => ({
      id: t.id,
      name: `${t.first_name || ''} ${t.last_name || ''}`.trim(),
    })).filter((t) => t.id && t.name)
  } catch (err) {
    console.error('[PreferredTechnicianField] load failed', err)
    technicians.value = []
  } finally {
    loading.value = false
  }
}

function onChange(value) {
  emit('update:modelValue', value == null || value === '' ? null : Number(value))
}

onMounted(loadTechnicians)
watch(() => props.modelValue, () => {
  if (!technicians.value.length) loadTechnicians()
})
</script>
